import React from 'react';
import type { ExperienceItem, Bullet } from '../../data/cv';
import { Bullets } from './Bullets';
import { CompanyMark } from './CompanyMark';
import { TechStack, InlineStack } from './TechStack';
import { periodEndpoints, splitLocation, roleDuration } from './helpers';
interface Props {
  item: ExperienceItem;
  isLast: boolean;
  /** One-page mode: trims summary/bullets/stack for a tighter footprint. */
  compact?: boolean;
}
/**
 * Editorial experience entry: a left "spine" carrying the index + year range,
 * and a right content well. On print each entry stays intact.
 */
export function ExperienceEntry({
  item,
  isLast,
  compact = false
}: Props) {
  const {
    start,
    end,
    same
  } = periodEndpoints(item.period);
  const {
    place,
    mode
  } = splitLocation(item.location);
  const duration = roleDuration(item.period);
  // In compact mode, flatten bullets (incl. subsections) and cap the count.
  const compactBullets: Bullet[] = compact ? [...(item.bullets ?? []), ...(item.subsections?.flatMap(s => s.bullets) ?? [])].slice(0, 2) : [];
  return <article className="cv-entry group relative grid grid-cols-[82px_1fr] gap-6" data-id="element-106" data-mp="1bxe65kcwpsbg">
      {/* spine: month/year range (clearly stacked) + duration */}
      <div className="flex flex-col items-end pt-[2px] text-right" data-id="element-107" data-mp="1fy6hcj5ca7ad">
        <span className="whitespace-nowrap font-mono text-[11px] font-medium tabular-nums leading-none text-[var(--ink)]" data-id="element-108" data-mp="1kq81sscnv4bh">
          {end}
        </span>
        {!same && <span className="mt-1 whitespace-nowrap font-mono text-[10px] tabular-nums leading-none text-[var(--ink-faint)]" data-id="element-109" data-mp="1unvvdeyj65s2">
            {start}
          </span>}
        {duration && <span className="mt-2 rounded-full bg-[var(--accent)]/10 px-1.5 py-[1px] font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-110" data-mp="1zl4bla04bzqo">
            {duration}
          </span>}
      </div>

      {/* timeline rail: a full-height line in the gutter + a node on this row.
                Absolute against the relative <article>; last role's line stops short. */}
      <div aria-hidden="true" className="absolute left-[94px] top-0 w-[1.5px] bg-[var(--line-strong)]" style={{
      bottom: isLast ? 'auto' : 0,
      height: isLast ? 20 : undefined
    }} data-id="element-111" data-mp="1cnjekfqq5s91" />
      <span aria-hidden="true" className="absolute left-[94px] top-[14px] z-10 h-2 w-2 -translate-x-1/2 rounded-full border-2 border-[var(--accent)] bg-[var(--paper)]" data-id="element-112" data-mp="1hk0ahw461awo" />

      {/* content */}
      <div className={isLast ? 'pb-0' : 'pb-8'} data-id="element-113" data-mp="1qk6lnbce5yso">
        {/* header: monogram + company / role, with meta on the right */}
        <div className="keep-with-next flex items-start gap-3.5" data-id="element-114" data-mp="259kxcq3fwsb8">
          <CompanyMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} data-id="element-115" data-mp="20arfyml18h5s" />

          <div className="min-w-0 flex-1" data-id="element-116" data-mp="2e8g01s79zk9s">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5" data-id="element-117" data-mp="29cqzpmnflzcf">
              <h3 className="font-display text-[19px] font-medium leading-tight text-[var(--ink)]" data-id="element-118" data-mp="2sx80h947xazo">
                {item.company}
              </h3>
              <div className="flex items-center gap-2.5" data-id="element-119" data-mp="2xuhuz36gscud">
                {item.industry && <span className="rounded-full bg-[var(--accent)]/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]" data-id="element-120" data-mp="2jyvzqe9n0wti">
                    {item.industry}
                  </span>}
                {mode && <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-faint)]" data-id="element-121" data-mp="2eyi2zsdhl9as">
                    {mode}
                  </span>}
              </div>
            </div>

            <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-soft)]" data-id="element-122" data-mp="258ggv64p20yi">
              {item.title}
              {place && <span className="ml-2 font-normal normal-case tracking-normal text-[var(--ink-faint)]" data-id="element-123" data-mp="1w7i7e8bhl18j">
                  · {place}
                </span>}
            </p>
          </div>
        </div>

        {/* body hangs under the header text, aligned past the monogram */}
        <div className="pl-[58px]" data-id="element-124" data-mp="1eg7s4lapdmyh">
          {/* COMPACT: only the first summary sentence + up to 3 flat bullets. */}
          {compact ? <>
              {item.summary?.[0] && <p className="mt-1.5 max-w-[72ch] text-[11px] leading-snug text-[var(--ink-soft)]" data-id="element-125" data-mp="15gkso51jar82">
                  {item.summary[0]}
                </p>}
              {compactBullets.length > 0 && <div className="mt-1.5 max-w-[72ch] gap-x-8 sm:columns-2" data-id="element-126" data-mp="viwu140t4zkm">
                  <Bullets items={compactBullets} data-id="element-127" data-mp="rgs2catx6d0m" />
                </div>}
              {item.stack && item.stack.length > 0 && <div className="mt-2" data-id="element-128" data-mp="2pv3j96qjdgvx">
                  <InlineStack stack={item.stack} data-id="element-129" data-mp="2lukop4x9dwq1" />
                </div>}
            </> : <>
              {item.summary && <div className="mt-3 max-w-[64ch] space-y-2" data-id="element-130" data-mp="7kfrif9nhlwt">
                  {item.summary.map((p, i) => <p key={i} className="text-[12.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-131" data-mp="cja7zak5aydr" data-mpi={i}>
                      {p}
                    </p>)}
                </div>}

              {item.bullets && <div className="mt-3.5 max-w-[68ch] gap-x-10 sm:columns-2" data-id="element-132" data-mp="3ps56w7dw6rj5">
                  <Bullets items={item.bullets} data-id="element-133" data-mp="3pu97ogghvaf7" />
                </div>}

              {item.subsections && <div className="mt-4 gap-x-10 sm:columns-2" data-id="element-134" data-mp="2nfka112ed44">
                  {item.subsections.map((sub, i) => <div key={i} className="mb-4 break-inside-avoid last:mb-0" data-id="element-135" data-mp="19411f5qil1vw" data-mpi={i}>
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]" data-id="element-136" data-mp="1e2m55f4z63p3">
                        {sub.heading}
                      </p>
                      <Bullets items={sub.bullets} data-id="element-137" data-mp="3a7tqvr09sz9n" />
                    </div>)}
                </div>}

              {/* tech stack — grouped, icon-bearing, consistent across roles */}
              {item.stack && item.stack.length > 0 && <TechStack stack={item.stack} data-id="element-138" data-mp="3f5d5is0vre24" />}
            </>}
        </div>
      </div>
    </article>;
}